import React, { useEffect, useMemo, useRef, useState } from 'react';
import { GripVertical, Image as ImageIcon, X } from 'lucide-react';
import { destinos, nuevoId } from '../../../lib/formularios';
import { useEditor } from './contexto';
import { GlifoOpcion, ImagenAdjunta, Selector } from './controles';

/*
 * Lista de opciones de Opción múltiple, Casillas y Desplegable.
 * Como en Google: Intro crea la siguiente, pegar varias líneas crea una opción por línea, se reordenan
 * arrastrando del asa y "Otro" es una fila aparte que no se escribe (quien responde pone su texto).
 */
export default function EditorOpciones({ item }) {
  const { cambiarItem, subirImagen, formulario } = useEditor();
  const opciones = useMemo(() => item.opciones || [], [item.opciones]);
  const refs = useRef({});
  const [enfocar, setEnfocar] = useState(null);
  const [arrastre, setArrastreEstado] = useState(null); // {id, destino, antesDe}
  const arrastreRef = useRef(null);
  const setArrastre = (valor) => {
    arrastreRef.current = valor;
    setArrastreEstado(valor);
  };
  const listaRef = useRef(null);

  useEffect(() => {
    if (!enfocar) return;
    const el = refs.current[enfocar];
    if (el) {
      el.focus();
      el.select();
    }
    setEnfocar(null);
  }, [enfocar, opciones]);

  const guardar = (nuevas, clave) => cambiarItem(item.id, { opciones: nuevas }, { clave });

  const textoPorDefecto = (lista) => {
    // "Opción N" sin repetir un número que ya esté (si borraste la 2, la nueva no vuelve a ser "Opción 2")
    let n = lista.length + 1;
    const usados = new Set(lista.map((o) => o.texto));
    while (usados.has(`Opción ${n}`)) n += 1;
    return `Opción ${n}`;
  };

  const agregar = (despuesDe = null, textos = null) => {
    const lista = [...opciones];
    const i = despuesDe ? lista.findIndex((o) => o.id === despuesDe) + 1 : lista.length;
    const nuevas = (textos || [null]).map((t) => ({ id: nuevoId(), texto: t ?? textoPorDefecto(lista), imagen: null, ir_a: 'siguiente' }));
    lista.splice(i, 0, ...nuevas);
    guardar(lista);
    setEnfocar(nuevas[nuevas.length - 1].id);
  };

  const quitar = (id) => {
    if (opciones.length <= 1) return;
    const i = opciones.findIndex((o) => o.id === id);
    const lista = opciones.filter((o) => o.id !== id);
    // Si la opción era correcta en la clave del cuestionario, deja de serlo
    const correctas = Array.isArray(item.clave?.correctas) ? item.clave.correctas.filter((c) => c !== id) : null;
    cambiarItem(item.id, correctas ? { opciones: lista, clave: { ...item.clave, correctas } } : { opciones: lista });
    const vecina = lista[Math.max(0, i - 1)];
    if (vecina) setEnfocar(vecina.id);
  };

  const cambiar = (id, campos, clave) => guardar(opciones.map((o) => (o.id === id ? { ...o, ...campos } : o)), clave);

  const alPegar = (e, opcion) => {
    const lineas = e.clipboardData.getData('text').split(/\r?\n/).map((s) => s.trim()).filter(Boolean);
    if (lineas.length < 2) return;
    e.preventDefault();
    const lista = [...opciones];
    const i = lista.findIndex((o) => o.id === opcion.id);
    lista[i] = { ...lista[i], texto: lineas[0] };
    const nuevas = lineas.slice(1).map((t) => ({ id: nuevoId(), texto: t, imagen: null, ir_a: 'siguiente' }));
    lista.splice(i + 1, 0, ...nuevas);
    guardar(lista);
    setEnfocar(nuevas[nuevas.length - 1].id);
  };

  const imagenDe = async (opcion) => {
    const img = await subirImagen();
    if (img) cambiar(opcion.id, { imagen: img });
  };

  // Arrastrar opciones: por el asa, con el puntero (sirve con ratón y con el dedo)
  const empezarArrastre = (e, id) => {
    e.preventDefault();
    const filas = () => [...(listaRef.current?.querySelectorAll('[data-opcion]') || [])];
    const mover = (ev) => {
      const y = ev.clientY;
      const resto = filas().filter((f) => f.dataset.opcion !== id);
      let destino = resto.length;
      for (let i = 0; i < resto.length; i += 1) {
        const r = resto[i].getBoundingClientRect();
        if (y < r.top + r.height / 2) {
          destino = i;
          break;
        }
      }
      setArrastre({ id, destino, antesDe: resto[destino]?.dataset.opcion || null });
    };
    const soltar = () => {
      window.removeEventListener('pointermove', mover);
      window.removeEventListener('pointerup', soltar);
      const a = arrastreRef.current;
      setArrastre(null);
      if (!a) return;
      const sin = opciones.filter((o) => o.id !== id);
      sin.splice(a.destino, 0, opciones.find((o) => o.id === id));
      if (sin.map((o) => o.id).join() !== opciones.map((o) => o.id).join()) guardar(sin);
    };
    window.addEventListener('pointermove', mover);
    window.addEventListener('pointerup', soltar);
  };

  const conSaltos = item.ir_segun_respuesta && item.tipo !== 'casillas';
  const listaDestinos = useMemo(() => [
    { valor: 'siguiente', texto: 'Continuar a la siguiente sección' },
    ...destinos(formulario.items).map((d) => ({ valor: d.id, texto: d.nombre })),
    { valor: 'enviar', texto: 'Enviar formulario' },
  ], [formulario.items]);

  const repetidas = useMemo(() => {
    const vistos = new Map();
    opciones.forEach((o) => {
      const t = (o.texto || '').trim().toLowerCase();
      if (t) vistos.set(t, (vistos.get(t) || 0) + 1);
    });
    return vistos;
  }, [opciones]);

  return (
    <div className="fe-opciones" ref={listaRef}>
      {opciones.map((o, i) => {
        const repetida = (repetidas.get((o.texto || '').trim().toLowerCase()) || 0) > 1;
        return (
          <div
            key={o.id}
            data-opcion={o.id}
            className={`fe-opcion ${arrastre?.id === o.id ? 'is-arrastrando' : ''} ${arrastre && arrastre.antesDe === o.id ? 'fe-soltar-antes' : ''}`}
          >
            <div className="fe-opcion-fila">
              {opciones.length > 1 && (
                <button
                  type="button"
                  className="fe-asa-opcion"
                  onPointerDown={(e) => empezarArrastre(e, o.id)}
                  aria-label="Arrastrar para reordenar"
                  tabIndex={-1}
                >
                  <GripVertical />
                </button>
              )}
              <GlifoOpcion tipo={item.tipo} indice={i} />
              <div className="fe-opcion-texto">
                <input
                  ref={(el) => { refs.current[o.id] = el; }}
                  className="fm-campo-linea fe-campo-opcion"
                  value={o.texto}
                  aria-label={`Opción ${i + 1}`}
                  onChange={(e) => cambiar(o.id, { texto: e.target.value }, `${item.id}:opcion:${o.id}`)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      agregar(o.id);
                    } else if (e.key === 'Backspace' && !o.texto && opciones.length > 1) {
                      e.preventDefault();
                      quitar(o.id);
                    }
                  }}
                  onPaste={(e) => alPegar(e, o)}
                />
                {repetida && <span className="fe-aviso-campo">Opción duplicada</span>}
              </div>
              <button type="button" className="fe-icono-btn fe-solo-hover" onClick={() => imagenDe(o)} aria-label="Añadir imagen a la opción" title="Añadir imagen">
                <ImageIcon />
              </button>
              {conSaltos && (
                <Selector
                  className="fe-select-destino"
                  value={o.ir_a || 'siguiente'}
                  onChange={(v) => cambiar(o.id, { ir_a: v })}
                  opciones={listaDestinos}
                  etiqueta={`Después de "${o.texto}"`}
                />
              )}
              {opciones.length > 1 && (
                <button type="button" className="fe-icono-btn" onClick={() => quitar(o.id)} aria-label={`Quitar ${o.texto || 'opción'}`} title="Quitar">
                  <X />
                </button>
              )}
            </div>
            <ImagenAdjunta imagen={o.imagen} alt={o.texto} className="fe-imagen-opcion" onQuitar={() => cambiar(o.id, { imagen: null })} />
          </div>
        );
      })}
      {arrastre && arrastre.antesDe === null && <div className="fe-soltar-final" />}

      {item.otra && item.tipo !== 'desplegable' && (
        <div className="fe-opcion">
          <div className="fe-opcion-fila">
            {opciones.length > 1 && <span className="fe-asa-hueco" />}
            <GlifoOpcion tipo={item.tipo} indice={opciones.length} />
            <div className="fe-opcion-texto">
              <input className="fm-campo-linea fe-campo-opcion" value="Otro…" disabled aria-label="Opción Otro" />
            </div>
            <button type="button" className="fe-icono-btn" onClick={() => cambiarItem(item.id, { otra: false })} aria-label="Quitar la opción Otro" title="Quitar">
              <X />
            </button>
          </div>
        </div>
      )}

      <div className="fe-opcion fe-opcion-nueva">
        <div className="fe-opcion-fila">
          {opciones.length > 1 && <span className="fe-asa-hueco" />}
          <GlifoOpcion tipo={item.tipo} indice={opciones.length + (item.otra ? 1 : 0)} />
          <div className="fe-opcion-texto fe-opcion-agregar">
            <button type="button" className="fe-agregar-opcion" onClick={() => agregar()}>Añadir opción</button>
            {item.tipo !== 'desplegable' && !item.otra && (
              <>
                <span className="fe-texto-2 fe-opcion-o">o</span>
                <button type="button" className="fe-btn-texto" onClick={() => cambiarItem(item.id, { otra: true })}>añadir "Otro"</button>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
